import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useFieldArray, useForm } from 'react-hook-form';

import {
  ActiveOnboardingStep,
  CreateSite,
  OnboardingLocation,
  OnboardingStep,
  Shift,
} from '@vyce/core/src/types';
import { getLocationDetails } from '@vyce/core/src/utils/location';
import { createShiftRequest, createSiteRequest } from '@vyce/core/src/api/time';
import { TimeModuleContext } from '@vyce/core/src/contexts';
import { EMPTY_ADDRESS, getEmptyShift } from '@vyce/core/src/modules/timeModule/constants';
import { locationToSite, shiftToSiteShift } from '@vyce/core/src/modules/timeModule/utils';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { isScheduleValid } from '../helpers/isScheduleValid';
import { buildShiftFieldName, parseStepName } from '../helpers/shiftStepName';
import { defaultValues, SHIFT_STEP, SHIFTS_FIELD } from '../constants';
import { NotificationContext } from '../../../../contexts/notificationContext';
import { useAddNewSiteManager } from '../../../../modules/timeModule/hooks/useAddNewSiteManager';
import { Props } from '../types';

export const useData = ({
  selectedCompany,
  user,
  initialStep,
  fetchCompanyData,
  getStaffRequest,
}: Props & {
  initialStep: {
    component: JSX.Element;
    name: string;
  };
}) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [activeStep, setActiveStep] = useState<ActiveOnboardingStep>({ step: initialStep, number: 0 });
  const [defaultSteps, setDefaultSteps] = useState<OnboardingStep[]>([]);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const childRef = useRef<any>();
  const [open, setOpen] = useState<boolean>(false);
  const [newLocationId, setNewLocationId] = useState<string>('');
  const [withLocation, setWithLocation] = useState<boolean>(true);
  const [disableNext, setDisableNext] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // Answer to "Add another shift to this location?", per shift index. A shift that isn't in here
  // hasn't been asked yet — which is what keeps the buttons live on the step the user is on.
  const [shiftAnswers, setShiftAnswers] = useState<Record<number, boolean>>({});
  // Set when a shift has just been added, so the wizard can jump to its first step once the
  // matching block of steps has been rendered.
  const [pendingShiftIndex, setPendingShiftIndex] = useState<number>();

  const { staff, syncManagers, setStaffSubstring } = useAddNewSiteManager({
    selectedCompanyId: selectedCompany?.uuid ?? '',
    locationId: '',
    isNewLocation: true,
    getStaffRequest,
  });

  const methods = useForm<OnboardingLocation>({
    defaultValues,
  });
  const { getLocations } = useContext(TimeModuleContext);
  const { control, setValue, watch, getValues, reset } = methods;
  const address = watch('address');
  const { fields: shiftFields, append: appendShift } = useFieldArray({ control, name: SHIFTS_FIELD });
  const shiftsCount = shiftFields.length;

  // Watching the overtime times by name keeps this cheap — subscribing to the whole `shifts`
  // array would re-render the entire wizard on every keystroke in any shift field.
  const overtimeFieldNames = useMemo(
    () =>
      Array.from({ length: shiftsCount }).flatMap((item, index) => [
        `${buildShiftFieldName(index)}.overtime.startTime`,
        `${buildShiftFieldName(index)}.overtime.endTime`,
      ]),
    [shiftsCount]
  );
  const overtimeTimes = watch(overtimeFieldNames as any) as any[];
  /** Shifts that have both an overtime start and end — they get the two extra overtime steps. */
  const shiftsWithOvertime = Array.from({ length: shiftsCount })
    .map((item, index) => index)
    .filter(index => overtimeTimes[index * 2] && overtimeTimes[index * 2 + 1]);
  /** Stable dependency for the effect that adds those steps — the array itself is rebuilt every render. */
  const shiftsWithOvertimeKey = shiftsWithOvertime.join(',');

  const { createLocationChat } = useChatRequests({
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    phone: user.phone,
    avatarUrl: user.photo?.url,
  });

  const nextStep = (jumpTo?: number) => {
    childRef?.current?.nextStep(jumpTo);
  };

  const scrollDown = () => {
    childRef?.current?.scrollDown();
  };

  const removeLocationQuestionSteps = () => {
    const newSteps = [...defaultSteps];
    newSteps.splice(2, 3);
    setSteps(newSteps);
  };

  const handleNoClick = (activeStep: number) => {
    setWithLocation(false);
    removeLocationQuestionSteps();
    if (activeStep < 2) {
      nextStep();
    }
    setValue('address', EMPTY_ADDRESS);
    setDisableNext(false);
  };

  const handleYesClick = (activeStepNumber: number) => {
    setWithLocation(true);
    if (activeStepNumber < 2) {
      nextStep();
    }
    setSteps(defaultSteps);
    setDisableNext(false);
  };

  const answerAddAnotherShift = useCallback((shiftIndex: number, addAnother: boolean) => {
    setShiftAnswers(answers => ({ ...answers, [shiftIndex]: addAnother }));
  }, []);

  const addAnotherShift = useCallback(
    (shiftIndex: number) => {
      answerAddAnotherShift(shiftIndex, true);
      appendShift(getEmptyShift());
      setPendingShiftIndex(shiftIndex + 1);
    },
    [answerAddAnotherShift, appendShift]
  );

  const skipAnotherShift = useCallback(
    (shiftIndex: number) => {
      answerAddAnotherShift(shiftIndex, false);
      nextStep();
    },
    [answerAddAnotherShift]
  );

  const clear = () => {
    setActiveStep({ step: initialStep, number: 0 });
    reset();
    setShiftAnswers({});
    setPendingShiftIndex(undefined);
    setSteps(defaultSteps);
    setOpen(false);
  };

  const prepareSite = (location: OnboardingLocation, shift: Shift): CreateSite => {
    const site = locationToSite(location);

    return {
      ...site,
      shift: shiftToSiteShift(shift),
    };
  };

  /**
   * `create site` only carries the first shift, so any extra ones the user stacked up in the
   * wizard are created against the site afterwards. Returns the names of the ones that failed —
   * the location itself already exists by then, so a failure here isn't worth throwing away.
   */
  const createExtraShifts = async (companyId: string, siteId: string, shifts: Shift[]) => {
    const failedShiftNames: string[] = [];

    for (let index = 0; index < shifts.length; index++) {
      const shift = shifts[index];
      try {
        await createShiftRequest({ companyId, siteId, shift: shiftToSiteShift(shift) });
      } catch (e) {
        failedShiftNames.push(shift.name || `shift ${index + 2}`);
      }
    }

    return failedShiftNames;
  };

  const createLocation = async (data: OnboardingLocation) => {
    if (!selectedCompany?.uuid || !data.shifts?.length) {
      return;
    }

    const [firstShift, ...extraShifts] = data.shifts;
    const site = prepareSite(data, firstShift);
    setLoading(true);
    try {
      const res = await createSiteRequest(selectedCompany.uuid, site);
      const location = res.data;

      const failedShiftNames = await createExtraShifts(selectedCompany.uuid, location.uuid, extraShifts);

      await syncManagers(data.locationManagers, location.uuid);

      createLocationChat({
        companyId: selectedCompany.uuid,
        locationId: location.uuid,
        locationName: location.name,
        creator: {
          uid: user.uuid,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          phone: user.phone,
          avatarUrl: user.photo?.url,
        },
      });

      if (failedShiftNames.length) {
        showNotification({
          message: `${location.name} was created, but we couldn’t add ${failedShiftNames.join(
            ', '
          )}. Please add ${failedShiftNames.length > 1 ? 'them' : 'it'} from the location’s settings.`,
          options: { variant: 'warning' },
        });
      }

      setNewLocationId(location.uuid);
      setOpen(true);
      getLocations(false);

      if (fetchCompanyData) {
        fetchCompanyData(selectedCompany.uuid);
      }
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  };

  const checkAddress = async (address_snippet?: string) => {
    if (!address_snippet) {
      return '';
    }
    const address = await getLocationDetails({
      fullAddress: address_snippet || '',
    });
    if (!address?.lat || !address?.lon) {
      showNotification({ message: 'Location not found...', options: { variant: 'error' } });
      return '';
    } else {
      return address;
    }
  };

  const handleNext = async (data: OnboardingLocation) => {
    const { step, shiftIndex = 0 } = parseStepName(activeStep.step?.name);

    switch (step) {
      case 'LocationNameStep':
        createLocation(data);
        break;
      case 'DoYouNeedSpecificLocationStep':
        if (!data.address?.address_snippet) {
          return nextStep();
        }
        const addressData = await checkAddress(data.address.address_snippet);
        if (addressData) {
          nextStep();
        }
        break;
      case SHIFT_STEP.addAnother:
        // Moving on with the main button is the same answer as "Not right now".
        setShiftAnswers(answers => ({ ...answers, [shiftIndex]: answers[shiftIndex] ?? false }));
        nextStep();
        break;
      case SHIFT_STEP.regularSchedule:
      case SHIFT_STEP.overtime:
      case SHIFT_STEP.breaks:
      case SHIFT_STEP.shift:
        const shiftFieldName = buildShiftFieldName(shiftIndex);
        const regularSchedule: any = getValues(`${shiftFieldName}.regular.schedule` as any);
        const overtimeSchedule: any = getValues(`${shiftFieldName}.overtime.schedule` as any);
        if (isScheduleValid(regularSchedule, overtimeSchedule)) {
          nextStep();
        }
        break;
      default:
        nextStep();
        break;
    }
  };

  const setAddressData = async (addressSnippet?: string) => {
    const addressData = await checkAddress(addressSnippet);
    if (addressData) {
      setValue('address', addressData);
    }
  };

  useEffect(() => {
    if (address?.address_snippet && !address?.lat && !address?.lon) {
      setAddressData(address.address_snippet);
    }
  }, [address?.address_snippet]);

  return {
    activeStep,
    withLocation,
    disableNext,
    loading,
    methods,
    steps,
    childRef,
    open,
    newLocationId,
    staff,
    shiftsCount,
    shiftsWithOvertime,
    shiftsWithOvertimeKey,
    shiftAnswers,
    pendingShiftIndex,
    setPendingShiftIndex,
    addAnotherShift,
    skipAnotherShift,
    setDefaultSteps,
    clear,
    setOpen,
    setSteps,
    handleNext,
    setActiveStep,
    scrollDown,
    handleNoClick,
    handleYesClick,
    setStaffSubstring,
  };
};
